'use client';

// React imports
import React, {
  useCallback,
  useRef,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
// Hook imports
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { useFilterSetUtils } from 'src/hooks/filters/useFilterSetUtils';
import { useEditDrawerData } from 'src/hooks/common/useEditDrawerData';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useGetFilterSetsQuery, useUpdateFilterSetMutation } from 'src/api/redux/filterSets';
import { useAppDispatch } from 'src/redux';
import { updateFilterSetData } from 'src/redux/slices/profileSelectionsSlice';
// Component imports
import { Drawer } from 'src/components/PulseUI/Drawer';
import { Button } from 'src/components/PulseUI/Button';
import { Specialties } from 'src/components/FilterSets/Specialties';
import { SiteSpecialties } from 'src/components/FilterSets/SiteSpecialties';
import { HospitalSystems } from 'src/components/FilterSets/HospitalSystems';
import { GPOAffiliations } from 'src/components/FilterSets/GPOAffiliations';
import { Fellowship } from 'src/components/FilterSets/Fellowship';
import { MedicalSchools } from 'src/components/FilterSets/MedicalSchools';
import { Residency } from 'src/components/FilterSets/Residency';
import { PayerType } from 'src/components/FilterSets/PayerType';
import { SiteOfCare } from 'src/components/FilterSets/SiteOfCare';
// Icon imports
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { IconButton } from 'src/components/PulseUI/IconButton';

// Type imports
import { DeleteFilterModal } from 'src/components/Discovery/components/FilterDrawer/components/ManageFilters/components';

interface EditFilterDrawerProps {
  location: string;
}

const EditFilterDrawer = ({ location }: EditFilterDrawerProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const dispatch = useAppDispatch();

  // RTK Query mutation hook - cache invalidation happens automatically
  const [updateFilterSet, { isLoading: isUpdating }] = useUpdateFilterSetMutation();

  const { isDrawerOpen, closeAllButCurrent, closeDrawer, getPreviousDrawerId } =
    useDrawer();
  const {
    setSelectedFilterPreset,
    setTempSelectedFilterPreset,
    tempSelectedFilterPreset,
  } = useTableFilterSelections(location);

  const { getCurrentFilterSetData, clearCurrentFilterSetData } =
    useEditDrawerData();

  // Get saved volume to refresh if this filter set is currently applied
  const { appliedSavedVolume, setSavedVolume } = useSavedVolume(location);
  const {
    appliedSavedVolume: discoveryVolume,
    setSavedVolume: setDiscoveryVolume,
  } = useSavedVolume(LOCATION.DISCOVERY);
  // Fetch filter sets with type CODE for reference
  useGetFilterSetsQuery({ type: 'CODE' });

  const {
    applyFilters,
    convertParentFilterSetToFilterSet,
    isDirty,
    getFiltersByParentType,
  } = useFilterSets(location);

  useFilterSetUtils({
    filterType: 'FILTER',
  });

  // Get the current filter set data
  const currentFilterSet = getCurrentFilterSetData();
  const changedFilterSet = getFiltersByParentType(currentFilterSet?.type);

  // Use a ref to track if we've already initialized the filter
  const initializedRef = useRef(false);

  // Create a consistent drawer ID
  const drawerId = useMemo(() => `editFilterDrawer-${location}`, [location]);

  // Check if drawer is open
  const isOpen = isDrawerOpen(drawerId);

  //Local State management
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Determine if there are unsaved changes
  const isFiltersDirty = useMemo(() => {
    return isDirty() || label !== currentFilterSet?.label;
  }, [label, isDirty, currentFilterSet]);

  const handleLabelChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(e.target.value);
    },
    []
  );

  // Initialize filter state after render but before browser paint
  useLayoutEffect(() => {
    // Only initialize once when the drawer opens
    if (isOpen && currentFilterSet && !initializedRef.current) {
      initializedRef.current = true;
      // Use the existing convertParentFilterSetToFilterSet function
      convertParentFilterSetToFilterSet(currentFilterSet);
      setLabel(currentFilterSet.label);
    }

    return () => {
      // Reset the initialization flag when the drawer closes
      if (!isOpen) {
        initializedRef.current = false;
      }
    };
  }, [isOpen, currentFilterSet, convertParentFilterSetToFilterSet, location]);

  // Handle drawer close
  const handleClose = useCallback(() => {
    setLabel('');
    setError(null);
    clearCurrentFilterSetData();
    const previousDrawerId = getPreviousDrawerId();

    if (
      previousDrawerId &&
      previousDrawerId !== `editFilterDrawer-${location}`
    ) {
      closeAllButCurrent(previousDrawerId);
    } else {
      closeDrawer(`editFilterDrawer-${location}`);
    }
  }, [
    clearCurrentFilterSetData,
    closeAllButCurrent,
    closeDrawer,
    getPreviousDrawerId,
    location,
  ]);

  const handleDelete = useCallback(async () => {
    if (!currentFilterSet) return null;
    // Set the showDeleteModal state to true to open the modal
    setShowDeleteModal(true);
  }, [currentFilterSet]);

  const handleCloseDeleteModal = useCallback(() => {
    if (currentFilterSet?.id === tempSelectedFilterPreset?.id) {
      setSelectedFilterPreset(null);
      setTempSelectedFilterPreset(null);
    }
    handleClose();
    setShowDeleteModal(false);
  }, [
    currentFilterSet?.id,
    tempSelectedFilterPreset?.id,
    setSelectedFilterPreset,
    setTempSelectedFilterPreset,
    handleClose,
  ]);

  const handleSave = useCallback(async () => {
    // Validate required data
    if (!changedFilterSet || !currentFilterSet?.id) {
      setError('Missing required filter data');
      return;
    }

    // Validate filter set name
    if (!label.trim()) {
      setError('Filter set name is required');
      return;
    }

    // Reset any previous errors
    setError(null);

    // Apply the current filter state
    applyFilters();

    try {
      const updatedFilterSetPayload = {
        ...changedFilterSet,
        label: label.trim(),
      };

      // Use RTK Query mutation - cache invalidation happens automatically
      await updateFilterSet({
        filterId: currentFilterSet.id,
        data: updatedFilterSetPayload,
      }).unwrap();

      // Create a properly typed FilterSet object for the UI update
      const newFilterSet = {
        ...currentFilterSet,
        ...changedFilterSet,
        label: label.trim(),
      };
      setSelectedFilterPreset(newFilterSet);
      setTempSelectedFilterPreset(newFilterSet);

      setNotification({
        title: 'Success',
        message: 'Filter set updated successfully',
        type: 'success',
      });

      // Create the updated filter set data
      const updatedFilterSetData = {
        ...currentFilterSet,
        ...changedFilterSet,
        label: label.trim(),
        filters: changedFilterSet.filters || currentFilterSet.filters,
      };

      // Force refresh saved volume if this filter set is currently applied
      const freshVolume = {
        ...updatedFilterSetData,
        volumeType: 'filterSet',
      };

      if (
        appliedSavedVolume &&
        appliedSavedVolume.id === currentFilterSet.id &&
        appliedSavedVolume.volumeType === 'filterSet'
      ) {
        setSavedVolume(null);
        setSavedVolume(freshVolume);
      }

      // Also update discovery location if this filter set is applied there
      if (
        discoveryVolume &&
        discoveryVolume.id === currentFilterSet.id &&
        discoveryVolume.volumeType === 'filterSet'
      ) {
        setDiscoveryVolume(null);
        setDiscoveryVolume(freshVolume);
      }

      // Update Redux store with the updated filter set data
      dispatch(updateFilterSetData({ updatedFilterSet: updatedFilterSetData }));
      handleClose();
    } catch (error: unknown) {
      // Type guard for error object
      const err = error as { error_message?: string };
      const errorMessage = err.error_message || 'Failed to update filter set';
      console.error('Error updating filter set:', errorMessage);
      setError(errorMessage);
      log.exception(`Error updating filter set: ${err}`);
    }
  }, [
    changedFilterSet,
    currentFilterSet,
    label,
    applyFilters,
    setSelectedFilterPreset,
    setTempSelectedFilterPreset,
    setNotification,
    log,
    updateFilterSet,
    dispatch,
    handleClose,
    appliedSavedVolume,
    setSavedVolume,
    discoveryVolume,
    setDiscoveryVolume,
  ]);

  const renderFilterSetContent = useCallback(() => {
    if (!currentFilterSet) return null;

    return (
      <>
        <SiteOfCare location={location} />
        <PayerType location={location} />
        <Specialties location={location} />
        <SiteSpecialties location={location} />
        <HospitalSystems location={location} />
        <GPOAffiliations location={location} />
        <MedicalSchools location={location} />
        <Residency location={location} />
        <Fellowship location={location} />
      </>
    );
  }, [currentFilterSet, location]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      hideBackdrop
      anchor="left"
      size="base"
      data-drawer="edit-filter-drawer"
    >
      <Drawer.Header
        className="bg-sky-100 text-primary border-b-0"
        data-testid={`editFilterDrawer-${location}-header`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p
              className="font-bold text-lg"
              data-testid={`editFilterDrawer-${location}-title`}
            >
              Edit Filter Set
            </p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleClose}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
              aria-label="Close edit filter drawer"
              data-testid={`editFilterDrawer-${location}-close-button`}
            />
          </div>
        </div>
      </Drawer.Header>
      <Drawer.Content>
        <div className="self-stretch p-4 inline-flex flex-col justify-start items-start gap-6">
          {/* Filter set name input */}
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <label
              htmlFor={`editFilterDrawer-${location}-filter-set-name`}
              className="self-stretch justify-start text-primary text-xs font-semibold font-['Inter'] leading-none"
            >
              Filter Set Name
            </label>
            <input
              id={`editFilterDrawer-${location}-filter-set-name`}
              type="text"
              value={label}
              onChange={handleLabelChange}
              className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 w-full text-primary text-base font-normal font-['Inter'] leading-normal"
              placeholder="Enter filter set name"
              required
              aria-required="true"
              data-testid={`editFilterDrawer-${location}-label`}
            />
          </div>
          <div className="self-stretch flex flex-col justify-start items-start gap-4">
            <div className="justify-start text-primary text-base font-bold font-['Inter'] leading-normal">
              Filters
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              {renderFilterSetContent()}
            </div>
            {/* Error message display */}
            {error && (
              <div
                className="w-full px-4 py-2 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm"
                role="alert"
                data-testid={`editFilterDrawer-${location}-error`}
              >
                {error}
              </div>
            )}
          </div>
        </div>
      </Drawer.Content>
      <Drawer.Footer className="!p-0 w-full bg-white border-t border-neutral-300 inline-flex flex-col justify-center items-start">
        <div className="flex items-center justify-between w-full gap-2 p-4">
          <Button
            variant="soft"
            color="danger"
            size="md"
            className="w-full self-stretch px-4 py-2.5 bg-red-50 rounded-full inline-flex justify-between items-center"
            onClick={handleDelete}
            aria-label="Delete filter set"
            data-testid={`editFilterDrawer-${location}-delete`}
            disabled={!currentFilterSet?.id}
          >
            Delete
          </Button>
          <Button
            variant="solid"
            color="primary"
            size="md"
            className="w-full self-stretch px-4 py-2.5 bg-sky-600 rounded-full inline-flex justify-between items-center"
            onClick={handleSave}
            loading={isUpdating}
            disabled={!isFiltersDirty}
            data-testid={`editFilterDrawer-${location}-update-and-apply`}
            aria-label="Update and apply filter set"
          >
            Update & Apply
          </Button>
        </div>
      </Drawer.Footer>
      {showDeleteModal && (
        <DeleteFilterModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          filterSet={currentFilterSet}
        />
      )}
    </Drawer>
  );
};

export default EditFilterDrawer;
